import { z } from 'zod'
import { publicIdSchema, utcTimestampSchema } from './primitives'
import { currentUserDtoSchema } from './user'

/**
 * A session record. The token itself is never part of this shape — only its
 * SHA-256 hash is stored, so a database dump cannot be replayed as a login.
 */
export const sessionSchema = z.object({
  id: publicIdSchema,
  userId: publicIdSchema,
  createdAt: utcTimestampSchema,
  lastSeenAt: utcTimestampSchema,
  /** Sliding idle expiry. */
  expiresAt: utcTimestampSchema,
  /** Hard expiry, never extended. */
  absoluteExpiresAt: utcTimestampSchema,
  revokedAt: utcTimestampSchema.nullable(),
  /** DATA-038/SEC-029 (Wave T): coarse "browser:platform" class, e.g. "chrome:windows". */
  clientLabel: z.string().nullable(),
})

export type Session = z.infer<typeof sessionSchema>

/** `GET /api/auth/session` — `user` is null when the caller is anonymous. */
export const sessionStateDtoSchema = z.object({
  authenticated: z.boolean(),
  user: currentUserDtoSchema.nullable(),
  expiresAt: utcTimestampSchema.nullable(),
})

export type SessionStateDto = z.infer<typeof sessionStateDtoSchema>

/**
 * Returned by a successful sign-in. `csrfToken` is echoed here purely for
 * convenience — it is also set as a readable cookie — and is not a credential:
 * it authorises nothing without the HttpOnly session cookie alongside it.
 */
export const authResultDtoSchema = sessionStateDtoSchema.extend({
  csrfToken: z.string().min(1),
})

export type AuthResultDto = z.infer<typeof authResultDtoSchema>
